import { useEffect, useLayoutEffect } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import { useIsTabletAndLarger } from 'signals/mediaQuery';
import { ZONE_IDS, ZONE, type AdvertZoneId } from 'components/sspAdverts/sspAdvertsConstants';
import { getAdSection } from 'components/sspAdverts/section';
import { useIsVisible } from 'components/sspAdverts/useIsVisible';
import { filters } from 'signals/filters';
import { type OfferUnion } from 'utils/isOfferType';
import { cn } from 'utils/cn';

type Props = {
	className?: string;
	mainOffer?: OfferUnion;
	elementId?: string;
	desktopCollocation?: string;
	mobileCollocation?: string;
	desktopZoneId?: AdvertZoneId;
	mobileZoneId?: AdvertZoneId;
	resetOnFiltersChange?: boolean;
};

export default function BottomAd({
	className,
	mainOffer,
	elementId = 'im-bottom',
	desktopCollocation,
	mobileCollocation,
	desktopZoneId = ZONE_IDS.PATICKA,
	mobileZoneId = ZONE_IDS.PATICKA_MOBIL,
	resetOnFiltersChange,
}: Props) {
	const isTabletAndLarger = useIsTabletAndLarger();
	const isVisible = useIsVisible(elementId, '600px');
	const isAdLoaded = useSignal(false);

	useLayoutEffect(() => {
		if (!isVisible.peek() || isAdLoaded.peek() || typeof globalThis.window.sssp?.getAds !== 'function') {
			return;
		}

		const zoneId = isTabletAndLarger.peek() ? desktopZoneId : mobileZoneId;
		const zone = ZONE[zoneId];
		if (!zone) {
			return;
		}

		const element = globalThis.document.getElementById(elementId);
		if (!element) {
			return;
		}
		element.innerHTML = '';
		element.style = ''; // Reset styles, so SSSP script can set them again

		globalThis.window.sssp.getAds({
			id: elementId,
			zoneId,
			width: zone.width,
			height: zone.height,
			options: {
				section: getAdSection(mainOffer, filters.peek()),
				...(mobileCollocation && !isTabletAndLarger.peek() && { collocation: mobileCollocation }),
				...(desktopCollocation && isTabletAndLarger.peek() && { collocation: desktopCollocation }),
			},
		});
		isAdLoaded.value = true;
	}, [isVisible.value, isTabletAndLarger.value, desktopCollocation, mobileCollocation]);

	useEffect(() => {
		if (!resetOnFiltersChange || !isAdLoaded.peek()) {
			return;
		}

		isAdLoaded.value = false;
	}, [filters.value, resetOnFiltersChange]);

	useLayoutEffect(() => {
		const resetIsAdLoaded = () => {
			isAdLoaded.value = false;
		};

		globalThis.document.addEventListener('astro:after-swap', resetIsAdLoaded);

		return () => {
			globalThis.document.removeEventListener('astro:after-swap', resetIsAdLoaded);
		};
	}, []);

	return (
		<div class={cn('mx-auto my-12 max-w-fit overflow-hidden', className)}>
			<div id={elementId} />
		</div>
	);
}
